import {
  CanAccess,
  CreateBase,
  Form,
  required,
  useGetList,
  useNotify,
} from "ra-core";
import { Navigate } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { TextInput } from "@/components/admin/text-input";

import { FormToolbar } from "../layout/FormToolbar";
import type { DealActivityType } from "../types";
import { AttemptCapInput } from "./AttemptCapInput";
import {
  ACTIVITY_NAME_MAX_LENGTH,
  MESSAGE_TEMPLATE_MAX_LENGTH,
  nextActivityTypePosition,
  toActivityTypeErrorMessage,
  validateActivityName,
  validateMessageTemplate,
} from "./activityTypeForm";
import { MessageTemplatePreview } from "./MessageTemplatePreview";

/**
 * Almikia Epic EA (TASK-113): "Nueva actividad" -- direccion sets a name, an
 * optional Spanish message template (with a live preview) and an optional
 * attempt cap (Código Azul = 3). The new activity is appended LAST in the
 * order (`nextActivityTypePosition`), mirroring
 * `DealStageConfigCreate.tsx`'s placement rule.
 */
export const DealActivityTypeCreate = () => (
  <CanAccess
    resource="deal_activity_types"
    action="create"
    accessDenied={<Navigate to="/" replace />}
  >
    <DealActivityTypeCreateContent />
  </CanAccess>
);

const DealActivityTypeCreateContent = () => {
  const notify = useNotify();
  const { data: activityTypes } = useGetList<DealActivityType>(
    "deal_activity_types",
    { pagination: { page: 1, perPage: 100 } },
  );

  if (!activityTypes) return null;

  const activeNames = activityTypes
    .filter((activity) => activity.archived_at == null)
    .map((activity) => activity.name);

  const transform = (data: {
    name: string;
    message_template?: string | null;
    max_attempts?: number | null;
  }) => ({
    name: data.name.trim(),
    message_template: data.message_template || null,
    max_attempts: data.max_attempts ?? null,
    position: nextActivityTypePosition(activityTypes),
  });

  return (
    <CreateBase
      redirect="list"
      transform={transform}
      mutationOptions={{
        onError: (error) =>
          notify(toActivityTypeErrorMessage(error), { type: "error" }),
      }}
    >
      <div className="mt-2 max-w-lg mx-auto">
        <Card>
          <CardContent>
            <Form className="space-y-4 pt-4">
              <TextInput
                source="name"
                label="resources.deal_activity_types.fields.name"
                validate={[
                  required(),
                  (value: string) => validateActivityName(value, activeNames),
                ]}
                maxLength={ACTIVITY_NAME_MAX_LENGTH}
                helperText={false}
              />
              <TextInput
                source="message_template"
                label="resources.deal_activity_types.fields.message_template"
                multiline
                rows={4}
                validate={validateMessageTemplate}
                maxLength={MESSAGE_TEMPLATE_MAX_LENGTH}
                helperText="resources.deal_activity_types.fields.message_template_help"
              />
              <MessageTemplatePreview source="message_template" />
              <AttemptCapInput source="max_attempts" />
              <FormToolbar />
            </Form>
          </CardContent>
        </Card>
      </div>
    </CreateBase>
  );
};
